import React from "react";
import { Browser } from "@capacitor/browser";
import { brand } from "../../theme/brand";
import { supabase, supabaseConfigured } from "../../supabase/client";
import { deleteAccount, resetPassword } from "../../supabase/auth";
import { getStorage, STORAGE_KEYS } from "../../data/storage";
import { StatusPill } from "../components/StatusPill";
import {
  mapSupabaseToSyncStatus,
  resolveAccountUiState,
} from "../../components/AccountBadge";
import { formatSyncTime } from "../../utils/formatSyncTime";
import { isNative, isNativeIOS } from "../../lib/platform";

const CLOUD_WIPE_CONFIRM_PHRASE = "WIPE MY CLOUD DATA";
const CLEAR_APP_DATA_CONFIRM_PHRASE = "CLEAR APP DATA";
const DELETE_ACCOUNT_CONFIRM_PHRASE = "DELETE MY ACCOUNT";

const btn: React.CSSProperties = {
  border: "1px solid rgba(255,255,255,0.1)",
  borderRadius: 10,
  background: "rgba(255,255,255,0.05)",
  color: "white",
  fontSize: "0.88rem",
  padding: "0.58rem 0.9rem",
  display: "inline-flex",
  alignItems: "center",
  gap: "0.4rem",
  cursor: "pointer",
  fontWeight: 700,
};

function StatusRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: "minmax(76px, 110px) minmax(0, 1fr)",
        gap: "0.5rem",
        alignItems: "center",
        padding: "0.42rem 0",
        borderBottom: "1px solid rgba(255,255,255,0.05)",
      }}
    >
      <span style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.83rem" }}>{label}</span>
      <span
        style={{
          color: "rgba(255,255,255,0.8)",
          fontSize: "0.83rem",
          fontWeight: 500,
          minWidth: 0,
          overflowWrap: "anywhere",
        }}
      >
        {children}
      </span>
    </div>
  );
}

type Props = {
  supabaseUserId: string | null;
  supabaseUserEmail: string | null;
  supabaseStatus: string;
  supabaseLastSyncAt: string | null;
  supabaseError: string | null;
  onSupabaseSyncNow: () => void;
  onSupabaseSignIn: () => void;
  onSupabaseSignOut: () => void;
  isDemoMode: boolean;
  isAuthenticated: boolean;
  resetAll: () => void;
};

export function AccountTabSection({
  supabaseUserId,
  supabaseUserEmail,
  supabaseStatus,
  supabaseLastSyncAt,
  supabaseError,
  onSupabaseSyncNow,
  onSupabaseSignIn,
  onSupabaseSignOut,
  isDemoMode,
  isAuthenticated,
  resetAll,
}: Props) {
  const disableWrites = isDemoMode;
  const isSignedIn = !!supabaseUserId;
  const [isMobile, setIsMobile] = React.useState(
    typeof window !== "undefined" && window.innerWidth < 640
  );

  // Change password state
  const [changePwBusy, setChangePwBusy] = React.useState(false);
  const [changePwMsg, setChangePwMsg] = React.useState<{ text: string; isError: boolean } | null>(null);
  const changePwTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  // Delete cloud data state
  const [cloudWipeState, setCloudWipeState] = React.useState<"idle" | "running" | "done" | "error">("idle");
  const [cloudWipeMsg, setCloudWipeMsg] = React.useState<string | null>(null);
  const [cloudWipeConfirmText, setCloudWipeConfirmText] = React.useState("");

  // Clear app data state
  const [clearAppState, setClearAppState] = React.useState<"idle" | "confirming" | "running">("idle");
  const [clearAppConfirmText, setClearAppConfirmText] = React.useState("");
  const [clearAppMsg, setClearAppMsg] = React.useState<string | null>(null);

  // Delete account state
  const [deleteAccountState, setDeleteAccountState] = React.useState<"idle" | "running" | "done" | "error">("idle");
  const [deleteAccountMsg, setDeleteAccountMsg] = React.useState<string | null>(null);
  const [deleteAccountConfirmText, setDeleteAccountConfirmText] = React.useState("");

  React.useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, []);

  React.useEffect(() => {
    return () => {
      if (changePwTimerRef.current) clearTimeout(changePwTimerRef.current);
    };
  }, []);

  const resolvedSyncStatus = mapSupabaseToSyncStatus(isSignedIn, supabaseStatus, supabaseError);
  const { statusText } = resolveAccountUiState(resolvedSyncStatus);
  const displayStatusText = statusText === "Cloud synced" ? "Synced" : statusText;
  const pillVariant: "synced" | "syncing" | "error" | "idle" =
    resolvedSyncStatus === "synced"  ? "synced"  :
    resolvedSyncStatus === "syncing" ? "syncing" :
    resolvedSyncStatus === "offline" ? "error"   : "idle";

  const handleChangePassword = async () => {
    if (!supabaseUserEmail) return;
    if (changePwTimerRef.current) clearTimeout(changePwTimerRef.current);
    setChangePwMsg(null);
    setChangePwBusy(true);
    try {
      const { error } = await resetPassword(supabaseUserEmail);
      if (error) throw error;
      setChangePwMsg({ text: "✓ Password reset email sent", isError: false });
      changePwTimerRef.current = setTimeout(() => setChangePwMsg(null), 6000);
    } catch {
      setChangePwMsg({ text: "Could not send reset email. Please try again.", isError: true });
    } finally {
      setChangePwBusy(false);
    }
  };

  const handleCloudWipe = async () => {
    if (!supabaseConfigured || !supabaseUserId) return;
    if (cloudWipeConfirmText.trim() !== CLOUD_WIPE_CONFIRM_PHRASE) {
      setCloudWipeState("error");
      setCloudWipeMsg(`Type "${CLOUD_WIPE_CONFIRM_PHRASE}" to confirm.`);
      return;
    }
    setCloudWipeState("running");
    setCloudWipeMsg(null);
    try {
      const userId = supabaseUserId;
      const [entriesRes, sprintsRes] = await Promise.all([
        supabase.from("entries").delete().eq("user_id", userId),
        supabase.from("sprints").delete().eq("user_id", userId),
      ]);
      if (entriesRes.error) throw new Error(`Entries: ${entriesRes.error.message}`);
      if (sprintsRes.error) throw new Error(`Sprints: ${sprintsRes.error.message}`);

      const { error: profileErr } = await supabase
        .from("profiles")
        .upsert(
          {
            id: userId,
            vitals: null,
            forecast: null,
            roadmaps: [],
            updated_at: new Date().toISOString(),
          },
          { onConflict: "id" }
        );
      if (profileErr) throw new Error(`Profile: ${profileErr.message}`);

      await Promise.resolve(onSupabaseSyncNow());
      setCloudWipeState("done");
      setCloudWipeMsg("Cloud data deleted. Re-import your backup JSON, then sync again.");
      setCloudWipeConfirmText("");
    } catch (err: any) {
      setCloudWipeState("error");
      setCloudWipeMsg(err?.message ?? "Cloud wipe failed.");
    }
  };

  const handleClearAppData = async () => {
    if (clearAppConfirmText.trim() !== CLEAR_APP_DATA_CONFIRM_PHRASE) {
      setClearAppMsg(`Type "${CLEAR_APP_DATA_CONFIRM_PHRASE}" to confirm.`);
      return;
    }
    setClearAppState("running");
    setClearAppMsg(null);
    resetAll();
  };

  const handleDeleteAccount = async () => {
    if (!supabaseConfigured || !supabaseUserId) return;
    if (deleteAccountConfirmText.trim() !== DELETE_ACCOUNT_CONFIRM_PHRASE) {
      setDeleteAccountState("error");
      setDeleteAccountMsg(`Type "${DELETE_ACCOUNT_CONFIRM_PHRASE}" to confirm.`);
      return;
    }
    setDeleteAccountState("running");
    setDeleteAccountMsg(null);
    try {
      await deleteAccount();
      const storage = getStorage();
      await Promise.all(
        Object.values(STORAGE_KEYS).map((k) => storage.delete(k).catch(() => {})),
      );
      try { window.localStorage.setItem("wt_landing_dismissed", "false"); } catch { /* ignore */ }
      setDeleteAccountState("done");
      setDeleteAccountMsg("Account deleted. Redirecting to landing page…");
      await Promise.resolve(onSupabaseSignOut());
      window.location.assign("/");
    } catch (err: any) {
      setDeleteAccountState("error");
      setDeleteAccountMsg(err?.message ?? "Account deletion failed. Try again.");
    }
  };

  const openLegalUrl = React.useCallback((url: string) => {
    if (typeof window === "undefined") return;
    if (isNative) {
      void Browser.open({ url, presentationStyle: "popover" });
      return;
    }
    window.open(url, "_blank", "noopener,noreferrer");
  }, []);

  const sectionStyle: React.CSSProperties = {
    border: "1px solid rgba(148,163,184,0.14)",
    borderRadius: 12,
    padding: "1rem 1.05rem",
    background: "rgba(255,255,255,0.015)",
    width: "100%",
    boxSizing: "border-box",
  };

  const subTitle: React.CSSProperties = {
    fontWeight: 700,
    fontSize: "0.93rem",
    color: "rgba(255,255,255,0.88)",
  };

  const subHelper: React.CSSProperties = {
    color: "rgba(255,255,255,0.5)",
    fontSize: "0.83rem",
    lineHeight: 1.45,
    margin: 0,
  };

  const cardTitleStyle: React.CSSProperties = {
    fontWeight: 700,
    fontSize: "0.93rem",
    color: "rgba(255,255,255,0.88)",
  };

  const dangerInputStyle: React.CSSProperties = {
    width: "100%",
    padding: "0.6rem 0.7rem",
    borderRadius: 10,
    border: "1px solid rgba(239,68,68,0.35)",
    background: "rgba(239,68,68,0.06)",
    color: "white",
    outline: "none",
    fontSize: "0.86rem",
    fontFamily: "monospace",
    boxSizing: "border-box",
  };

  const showCloudDanger = isAuthenticated && !isDemoMode;
  const showClearAppData = !isNativeIOS;
  const showDangerZone = showCloudDanger || showClearAppData;

  return (
    <div style={{ display: "grid", gap: "1.5rem", width: "100%", maxWidth: 620 }}>

      {/* ── Account actions ── (only when signed in) */}
      {isSignedIn && (
        <section style={{ ...sectionStyle, display: "grid", gap: isMobile ? "0.7rem" : "1rem" }}>
          <div style={cardTitleStyle}>Account</div>

          <div style={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
            <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
              <button
                style={btn}
                onClick={() => { void handleChangePassword(); }}
                disabled={disableWrites || changePwBusy}
                title={disableWrites ? "Disabled in Demo" : ""}
              >
                {changePwBusy ? "Sending…" : "Change Password"}
              </button>
              <button
                style={{
                  ...btn,
                  background: "rgba(239,68,68,0.09)",
                  borderColor: "rgba(239,68,68,0.22)",
                  color: "#fca5a5",
                }}
                onClick={onSupabaseSignOut}
                disabled={disableWrites}
                title={disableWrites ? "Disabled in Demo" : ""}
              >
                Sign Out
              </button>
            </div>

            {changePwMsg && (
              <span style={{
                fontSize: "0.83rem",
                color: changePwMsg.isError ? "#fca5a5" : "#86efac",
              }}>
                {changePwMsg.text}
              </span>
            )}
          </div>
        </section>
      )}

      {/* ── Cloud Sync ── */}
      <section style={{ ...sectionStyle, display: "grid", gap: isMobile ? "0.7rem" : "1rem" }}>
        <div style={cardTitleStyle}>Cloud Sync</div>

        <div
          style={{
            border: "1px solid rgba(255,255,255,0.07)",
            borderRadius: 12,
            overflow: "hidden",
            background: "rgba(255,255,255,0.018)",
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          <div style={{ padding: "0.6rem 0.85rem" }}>
            <StatusRow label="Status">
              <StatusPill variant={pillVariant} label={displayStatusText} />
            </StatusRow>
            {supabaseUserEmail && (
              <StatusRow label="Account">
                {supabaseUserEmail}
              </StatusRow>
            )}
            <StatusRow label="Last sync">
              {formatSyncTime(supabaseLastSyncAt)}
            </StatusRow>
            {supabaseError && (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: isMobile ? "92px 1fr" : "140px 1fr",
                  gap: "0.5rem",
                  alignItems: "center",
                  padding: "0.42rem 0",
                }}
              >
                <span style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.83rem" }}>Last error</span>
                <span style={{ fontSize: "0.83rem", color: "#fca5a5", fontWeight: 500 }}>
                  {supabaseError}
                </span>
              </div>
            )}
          </div>
        </div>

        {!isSignedIn && (
          <div style={{ color: "rgba(255,255,255,0.38)", fontSize: "0.82rem" }}>
            Sign in to sync across devices.
          </div>
        )}

        <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap" }}>
          {isSignedIn ? (
            <button
              style={btn}
              onClick={onSupabaseSyncNow}
              disabled={disableWrites}
              title={disableWrites ? "Disabled in Demo" : ""}
            >
              Sync now
            </button>
          ) : (
            <button
              style={{
                ...btn,
                background: brand.accent.primary,
                borderColor: "rgba(59,130,246,0.6)",
                color: "white",
              }}
              onClick={onSupabaseSignIn}
              disabled={disableWrites}
              title={disableWrites ? "Disabled in Demo" : ""}
            >
              Sign in / Create account
            </button>
          )}
        </div>
      </section>

      {/* ── Legal ── */}
      <section style={{ ...sectionStyle, display: "grid", gap: "0.75rem" }}>
        <div style={subTitle}>Legal</div>
        <p style={subHelper}>Read our Terms and Privacy Policy in your browser.</p>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr" : "repeat(2, minmax(0, max-content))",
            gap: "0.5rem",
          }}
        >
          <button style={btn} onClick={() => openLegalUrl("https://weightplanner.fit/terms")}>
            Terms of Service
          </button>
          <button style={btn} onClick={() => openLegalUrl("https://weightplanner.fit/privacy")}>
            Privacy Policy
          </button>
        </div>
      </section>

      {/* ── Danger Zone ── */}
      {showDangerZone && (
        <section
          style={{
            border: "1px solid rgba(239,68,68,0.28)",
            borderRadius: 12,
            background: "rgba(239,68,68,0.04)",
            padding: "1rem 1.1rem",
            display: "grid",
            gap: "1.5rem",
            width: "100%",
            boxSizing: "border-box",
          }}
        >
          <div
            style={{
              fontWeight: 800,
              fontSize: "0.8rem",
              textTransform: "uppercase" as const,
              letterSpacing: "0.08em",
              color: "rgba(239,68,68,0.7)",
            }}
          >
            Danger Zone
          </div>

          {/* Delete Cloud Data */}
          {showCloudDanger && (
            <div style={{ display: "grid", gap: "0.75rem" }}>
              <div>
                <div style={{ ...subTitle, color: "rgba(239,68,68,0.85)" }}>Delete Cloud Data</div>
                <p style={{ ...subHelper, marginTop: "0.2rem" }}>
                  Wipes all entries, sprints, and roadmap from the server. Local data stays.
                </p>
              </div>
              <div style={{ display: "grid", gap: "0.5rem", maxWidth: 420 }}>
                <label style={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.45)" }}>
                  Type{" "}
                  <span style={{ fontFamily: "monospace", color: "#fecaca" }}>
                    {CLOUD_WIPE_CONFIRM_PHRASE}
                  </span>{" "}
                  to confirm
                </label>
                <input
                  value={cloudWipeConfirmText}
                  onChange={(e) => setCloudWipeConfirmText(e.target.value)}
                  placeholder={CLOUD_WIPE_CONFIRM_PHRASE}
                  style={dangerInputStyle}
                />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
                <button
                  style={{
                    ...btn,
                    background: "rgba(239,68,68,0.16)",
                    borderColor: "rgba(239,68,68,0.34)",
                    color: "#fecaca",
                    opacity: cloudWipeState === "running" ? 0.6 : 1,
                    cursor: cloudWipeState === "running" ? "not-allowed" : "pointer",
                  }}
                  onClick={() => { void handleCloudWipe(); }}
                  disabled={cloudWipeState === "running"}
                >
                  {cloudWipeState === "running"
                    ? "Wiping cloud…"
                    : cloudWipeState === "done"
                    ? "Cloud data deleted ✓"
                    : "Delete Cloud Data"}
                </button>
                {cloudWipeMsg && (
                  <span
                    style={{
                      fontSize: "0.82rem",
                      color: cloudWipeState === "error" ? "#fca5a5" : "#86efac",
                      maxWidth: 420,
                      lineHeight: 1.4,
                    }}
                  >
                    {cloudWipeMsg}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Divider between Delete Cloud Data and Clear App Data */}
          {showCloudDanger && showClearAppData && (
            <div style={{ borderTop: "1px solid rgba(239,68,68,0.15)" }} />
          )}

          {/* Clear App Data (web only) */}
          {showClearAppData && (
            <div style={{ display: "grid", gap: "0.75rem" }}>
              <div>
                <div style={{ ...subTitle, color: "rgba(239,68,68,0.85)" }}>Clear App Data</div>
                <p style={{ ...subHelper, marginTop: "0.2rem" }}>
                  Resets the app on this device. Your account and cloud data are unaffected.
                </p>
              </div>
              <div style={{ display: "grid", gap: "0.5rem", maxWidth: 420 }}>
                <label style={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.45)" }}>
                  Type{" "}
                  <span style={{ fontFamily: "monospace", color: "#fecaca" }}>
                    {CLEAR_APP_DATA_CONFIRM_PHRASE}
                  </span>{" "}
                  to confirm
                </label>
                <input
                  value={clearAppConfirmText}
                  onChange={(e) => { setClearAppConfirmText(e.target.value); setClearAppMsg(null); }}
                  placeholder={CLEAR_APP_DATA_CONFIRM_PHRASE}
                  disabled={clearAppState === "running"}
                  style={dangerInputStyle}
                />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
                <button
                  style={{
                    ...btn,
                    background: "rgba(239,68,68,0.16)",
                    borderColor: "rgba(239,68,68,0.34)",
                    color: "#fecaca",
                    opacity: clearAppState === "running" ? 0.6 : 1,
                    cursor: clearAppState === "running" ? "not-allowed" : "pointer",
                  }}
                  onClick={() => { void handleClearAppData(); }}
                  disabled={clearAppState === "running"}
                >
                  {clearAppState === "running" ? "Clearing…" : "Clear App Data"}
                </button>
                {clearAppMsg && (
                  <span style={{ fontSize: "0.82rem", color: "#fca5a5", maxWidth: 420, lineHeight: 1.4 }}>
                    {clearAppMsg}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Divider between Clear App Data (or Delete Cloud Data) and Delete Account */}
          {showCloudDanger && (showClearAppData || true) && (
            <div style={{ borderTop: "1px solid rgba(239,68,68,0.15)" }} />
          )}

          {/* Delete Account */}
          {showCloudDanger && (
            <div style={{ display: "grid", gap: "0.75rem" }}>
              <div>
                <div style={{ ...subTitle, color: "rgba(239,68,68,0.85)" }}>Delete Account</div>
                <p style={{ ...subHelper, marginTop: "0.2rem" }}>
                  Permanently deletes your account and all data everywhere. This cannot be undone.
                </p>
              </div>
              <div style={{ display: "grid", gap: "0.5rem", maxWidth: 420 }}>
                <label style={{ fontSize: "0.78rem", color: "rgba(255,255,255,0.45)" }}>
                  Type{" "}
                  <span style={{ fontFamily: "monospace", color: "#fecaca" }}>
                    {DELETE_ACCOUNT_CONFIRM_PHRASE}
                  </span>{" "}
                  to confirm
                </label>
                <input
                  value={deleteAccountConfirmText}
                  onChange={(e) => setDeleteAccountConfirmText(e.target.value)}
                  placeholder={DELETE_ACCOUNT_CONFIRM_PHRASE}
                  disabled={deleteAccountState === "running" || deleteAccountState === "done"}
                  style={dangerInputStyle}
                />
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
                <button
                  style={{
                    ...btn,
                    background: "rgba(239,68,68,0.22)",
                    borderColor: "rgba(239,68,68,0.45)",
                    color: "#fecaca",
                    opacity:
                      deleteAccountState === "running" || deleteAccountState === "done" ? 0.6 : 1,
                    cursor:
                      deleteAccountState === "running" || deleteAccountState === "done"
                        ? "not-allowed"
                        : "pointer",
                  }}
                  onClick={() => { void handleDeleteAccount(); }}
                  disabled={deleteAccountState === "running" || deleteAccountState === "done"}
                >
                  {deleteAccountState === "running"
                    ? "Deleting account…"
                    : deleteAccountState === "done"
                    ? "Account deleted ✓"
                    : "Delete Account"}
                </button>
                {deleteAccountMsg && (
                  <span
                    style={{
                      fontSize: "0.82rem",
                      color: deleteAccountState === "error" ? "#fca5a5" : "#86efac",
                      maxWidth: 420,
                      lineHeight: 1.4,
                    }}
                  >
                    {deleteAccountMsg}
                  </span>
                )}
              </div>
            </div>
          )}
        </section>
      )}
    </div>
  );
}
